import { useMemo, useCallback } from 'react'
import {
  collection,
  query,
  where,
  orderBy,
  doc,
} from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { deleteDoc } from '../lib/firestore'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { runMutation } from '../lib/firestoreMutation'
import { track } from '../lib/analytics'
import { joinGroup } from '../lib/groupJoin'
import type { GroupInvite } from '../types'
import { fbt } from 'fbtee'

export function usePendingGroupInvites(userId: string | null) {
  const q = useMemo(
    () =>
      userId
        ? (query(
            collection(db, 'groupInvites'),
            where('toUserId', '==', userId),
            orderBy('createdAt', 'desc'),
          ) as Query<GroupInvite>)
        : null,
    [userId],
  )

  const { data: pendingInvites, loading } = useFirestoreCollection(q)

  const acceptInvite = useCallback(
    async (invite: GroupInvite) => {
      await runMutation(fbt('Failed to join group', 'Error toast when accepting a group invite fails'), async () => {
        await joinGroup({ groupInviteId: invite.id })
        track('group_invite_accepted', { group_id: invite.groupId })
      })
    },
    [],
  )

  const rejectInvite = useCallback(async (invite: GroupInvite) => {
    await runMutation(fbt('Failed to decline invite', 'Error toast when declining a group invite fails'), async () => {
      await deleteDoc(doc(db, 'groupInvites', invite.id))
      track('group_invite_rejected', { group_id: invite.groupId })
    })
  }, [])

  return { pendingInvites, loading, acceptInvite, rejectInvite }
}
